import { SignupSubmission } from "protogen/signup_service_pb";
import { RematchSession } from "protogen/advisors_service_pb";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Collapse,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useRunWorkflow } from "services/workflow";
import { RunWorkflowRequest } from "protogen/workflow_service_pb";

const DEFAULT_QUERY_TEXT = `
You are a matchmaking expert for a personal assistant service. I am going to give you a client lead and some information/notes about them. I am also going to give you a candidate for their assistant.  Your job is to provide a clear and concise description of if this candidate is a good match for the client. Dig deep into the lead’s description to make sure we are hitting on what they’ve told us they need.

Examples of great "reasons" for a candidate score:
- "Hyper-local and has special needs children of her own."
- "Close proximity and is a regular volunteer for ‘Autism Speaks’."
- "Originally from Bellingham and has experience planning detailed meals."
- "Has two boys who also play hockey"
- “Client asked for bilingual and this assistant speaks spanish”
- “They both grew up in the same area”
- “Client is an author and the candidate is a librarian.”
- "Has a background in Big Law and will understand how to balance client's career needs with home"
- "Grew up with horses and understands 'Horse People'"

Additionally - note “bad” matches:
- “Kip would be a poor fit as he doesn’t cook”
- “The client wants someone professional, but Jen acts as more of a helpful friend”
- “Client wants someone friendly but Mary is very blunt with clients”
- “Client said ‘spanish speaking’ is a dealbreaker and this candidate doesn’t speak spanish

The responses should be between 60-120 characters and don't need to note their location as we already include the geographic distance. Rather - you should note similarities or personality that may mesh well together. Focus on a description that isn't bland and will excite both sides about the match. Don't say "good at travel" if the client needs travel help, instead say "Jill is adventurous having sailed on three oceans" (if that is in her bio). Use the candidate’s name instead of “The candidate” or them.
`.trim();

export type MatchReason = {
  candidate_ref: string;
  score: number;
  reason: string;
};

export type MatchReasonMap = Record<string, MatchReason>;

type Props = {
  lead?: SignupSubmission;
  rematchSession?: RematchSession;
  open: boolean;
  setOpen: (open: boolean) => void;
  setRunning: (r: boolean) => void;
  setMatches: (matches: MatchReasonMap) => void;
  runEagerly?: boolean;
};
export default ({
  lead,
  rematchSession,
  open,
  setOpen,
  setRunning,
  setMatches,
  runEagerly = false,
}: Props) => {
  const { request: workflowRequest, loading } = useRunWorkflow();
  const [query, setQuery] = useState(DEFAULT_QUERY_TEXT);
  const handleGenerate = async () => {
    if (loading) {
      return;
    }
    setRunning(true);

    // Determine workflow and entity based on lead vs rematch session
    const isRematch = !!rematchSession;
    const workflowName = isRematch
      ? "generate-rematch-ranking"
      : "generate-matchmaking-ranking";
    const entityRef = isRematch ? rematchSession.ref : lead?.ref || "";

    const resp = await workflowRequest(
      new RunWorkflowRequest({
        workflowName: workflowName,
        arguments: [
          {
            name: "match_description_prompt",
            value: { case: "stringValue", value: query },
          },
        ],
        entityType: 0, // Not needed here.
        entityRef: entityRef,
        runAsync: false,
      }),
    );
    if (resp) {
      // The response from RunWorkflow is already parsed from JSON
      const results = resp.result ? JSON.parse(resp.result).results : [];
      if (!Array.isArray(results)) {
        console.error("Expected results to be an array but got:", results);
        return;
      }
      setMatches(Object.fromEntries(results.map((u) => [u.candidate_ref, u])));
    }
    setRunning(false);
    setOpen(false);
  };

  useEffect(() => {
    if (runEagerly) {
      handleGenerate();
    }
  }, []);

  return (
    <Collapse in={open}>
      <Box sx={{ position: "relative" }}>
        <TextField
          fullWidth
          multiline
          minRows={3} // Adjust as needed for initial height
          variant="outlined"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{
                  position: "absolute",
                  bottom: 16, // Adjust for padding within TextField
                  right: 16, // Adjust for padding within TextField
                  // Ensure adornment doesn't shrink parent
                  height: "auto",
                  alignItems: "flex-end",
                }}
              >
                <Button variant="contained" onClick={handleGenerate}>
                  {loading ? "Generating..." : "Generate"}
                </Button>
              </InputAdornment>
            ),
            sx: {
              // Add padding to the bottom of the input to make space for the button
              paddingBottom: "50px", // Adjust as needed
            },
          }}
        />
      </Box>
    </Collapse>
  );
};
